/**
 * MediaDetailPage — Page detail d'un media avec 3 onglets (Bloc finalisation Media).
 * Route : /dashboard/media/:id
 *
 * Onglets :
 *   1. General   : title/alt_text/captions/credit (author/source/license/rights_notice)
 *   2. Confidentialite : BlurZoneEditor + RGPD (note + status)
 *   3. Watermark : toggle ITERCATUS + apercu avant/apres
 *
 * IMPORTANT: original_cloudinary_url reste immuable pendant toute la session.
 */
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  getMedia,
  updateMediaMeta,
  setMediaWatermark,
} from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Skeleton } from "../components/ui/skeleton";
import BlurZoneEditor from "../components/BlurZoneEditor";
import { ArrowLeft, Save, Info, Shield, Droplet, AlertCircle } from "lucide-react";

const CREDIT_SOURCES = { itercatus: "ITERCATUS", partner: "Partenaire", user: "Utilisateur", other: "Autre" };

export default function MediaDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [media, setMedia] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("general");

  const load = async () => {
    setLoading(true);
    try {
      const res = await getMedia(id);
      setMedia(res.data);
    } catch {
      toast.error("Média introuvable");
      navigate("/dashboard/photos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading || !media) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-8 w-1/3" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Skeleton className="aspect-square" />
          <Skeleton className="h-96" />
        </div>
      </div>
    );
  }

  const isVideo = media.type === "video";

  return (
    <div className="p-6" data-testid="media-detail-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <Button variant="ghost" onClick={() => navigate("/dashboard/photos")} className="gap-2 mb-2" data-testid="btn-back-media">
            <ArrowLeft className="h-4 w-4" /> Retour à la médiathèque
          </Button>
          <h1 className="font-heading text-2xl font-semibold text-primary">
            {media.captions?.fr || media.alt_text?.fr || `Média · ${media.id.slice(0, 8)}`}
          </h1>
          <div className="text-xs text-muted-foreground mt-1">
            <code>{media.id}</code> · type: {media.type} · stockage: {media.storage_type}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Left panel: preview */}
        <div className="bg-white border border-border rounded-lg p-4">
          <div className="text-xs font-semibold text-primary uppercase tracking-wide mb-2">Aperçu (public)</div>
          {isVideo ? (
            <video src={media.public_cloudinary_url} poster={media.poster_url} controls className="w-full rounded" data-testid="media-preview-video" />
          ) : (
            <img
              src={media.public_cloudinary_url || media.original_cloudinary_url}
              alt=""
              className="w-full rounded object-contain max-h-[70vh]"
              data-testid="media-preview-image"
            />
          )}
          <div className="mt-3 text-xs">
            <div className="text-muted-foreground truncate">public : <code>{(media.public_cloudinary_url || "").slice(-80)}</code></div>
            {media.public_cloudinary_url !== media.original_cloudinary_url && (
              <div className="text-muted-foreground truncate mt-1">original : <code>{(media.original_cloudinary_url || "").slice(-80)}</code></div>
            )}
          </div>
        </div>

        {/* Right panel: tabs */}
        <div className="bg-white border border-border rounded-lg p-4">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="grid grid-cols-3 w-full">
              <TabsTrigger value="general" data-testid="tab-general"><Info className="h-4 w-4 mr-1" /> Général</TabsTrigger>
              <TabsTrigger value="privacy" data-testid="tab-privacy"><Shield className="h-4 w-4 mr-1" /> Confidentialité</TabsTrigger>
              <TabsTrigger value="watermark" data-testid="tab-watermark"><Droplet className="h-4 w-4 mr-1" /> Watermark</TabsTrigger>
            </TabsList>

            <TabsContent value="general" className="pt-4">
              <GeneralTab media={media} onSaved={(m) => setMedia(m)} />
            </TabsContent>

            <TabsContent value="privacy" className="pt-4">
              <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm text-blue-900 mb-4 flex gap-2">
                <Shield className="h-4 w-4 shrink-0 mt-0.5" />
                <span>
                  Les zones définies ci-dessous permettent de masquer visages, plaques d'immatriculation ou tout élément privé.
                  L'image originale reste intacte, seule la version publique est modifiée.
                </span>
              </div>
              {isVideo ? (
                <p className="text-sm text-muted-foreground">Le floutage vidéo n'est pas encore supporté.</p>
              ) : (
                <BlurZoneEditor media={media} onSaved={(m) => setMedia(m)} />
              )}
            </TabsContent>

            <TabsContent value="watermark" className="pt-4">
              <WatermarkTab media={media} onSaved={(m) => setMedia(m)} />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}

// -------------------- GENERAL TAB --------------------
function GeneralTab({ media, onSaved }) {
  const [form, setForm] = useState({
    caption_fr: media.captions?.fr || "",
    alt_fr: media.alt_text?.fr || "",
    credit_author: media.credit?.author || "",
    credit_source: media.credit?.source || "itercatus",
    credit_license: media.credit?.license || "",
    credit_rights_notice: media.credit?.rights_notice || "",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        captions: { ...(media.captions || {}), fr: form.caption_fr },
        alt_text: { ...(media.alt_text || {}), fr: form.alt_fr },
        credit: {
          author: form.credit_author || null,
          source: form.credit_source || null,
          license: form.credit_license || null,
          rights_notice: form.credit_rights_notice || null,
        },
      };
      const res = await updateMediaMeta(media.id, payload);
      toast.success("Métadonnées enregistrées");
      onSaved(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3">
      <div>
        <label className="block text-sm font-medium mb-1">Légende (fr)</label>
        <Input value={form.caption_fr} onChange={(e) => setForm({ ...form, caption_fr: e.target.value })} data-testid="input-caption-fr" />
      </div>
      <div>
        <label className="block text-sm font-medium mb-1">Texte alternatif (fr)</label>
        <Input value={form.alt_fr} onChange={(e) => setForm({ ...form, alt_fr: e.target.value })} data-testid="input-alt-fr" />
      </div>
      <div className="pt-2 border-t border-border">
        <div className="text-xs font-semibold text-primary uppercase tracking-wide mb-2">Crédits</div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium mb-1">Auteur</label>
            <Input value={form.credit_author} onChange={(e) => setForm({ ...form, credit_author: e.target.value })} data-testid="input-credit-author" />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1">Source</label>
            <Select value={form.credit_source} onValueChange={(v) => setForm({ ...form, credit_source: v })}>
              <SelectTrigger data-testid="select-credit-source"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(CREDIT_SOURCES).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-xs font-medium mb-1">Licence</label>
            <Input value={form.credit_license} onChange={(e) => setForm({ ...form, credit_license: e.target.value })} placeholder="ex : CC-BY-4.0" />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1">Mention droits</label>
            <Input value={form.credit_rights_notice} onChange={(e) => setForm({ ...form, credit_rights_notice: e.target.value })} />
          </div>
        </div>
      </div>
      <Button onClick={save} disabled={saving} className="gap-2" data-testid="btn-save-general">
        <Save className="h-4 w-4" /> {saving ? "..." : "Enregistrer"}
      </Button>
    </div>
  );
}

// -------------------- WATERMARK TAB --------------------
function WatermarkTab({ media, onSaved }) {
  const [applied, setApplied] = useState(!!media.watermark?.apply);
  const [saving, setSaving] = useState(false);

  const toggle = async (v) => {
    setApplied(v);
    setSaving(true);
    try {
      const res = await setMediaWatermark(media.id, { apply: v });
      toast.success(v ? "Watermark appliqué" : "Watermark retiré");
      onSaved(res.data);
    } catch (e) {
      setApplied(!v); // rollback
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const publicHasWatermark = (media.public_cloudinary_url || "").includes("l_itercatus_watermark");

  return (
    <div className="space-y-4">
      <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-900 flex gap-2">
        <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
        <div>
          <div className="font-semibold">Prérequis Cloudinary</div>
          <div className="text-xs mt-1">
            Le logo transparent doit être uploadé dans Cloudinary avec l'ID public <code>itercatus_watermark</code>.
            Si ce logo n'existe pas encore, l'URL publique retournera une erreur d'image côté navigateur (l'endpoint reste fonctionnel).
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3 p-3 border border-border rounded">
        <Switch checked={applied} onCheckedChange={toggle} disabled={saving} data-testid="switch-watermark" />
        <div className="flex-1">
          <div className="font-semibold text-sm">Appliquer le watermark ITERCATUS</div>
          <div className="text-xs text-muted-foreground">Position : bas-droite · Opacité : 60% · Taille : 15% largeur</div>
        </div>
        <span className={`text-xs px-2 py-1 rounded ${publicHasWatermark ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-600"}`}>
          {publicHasWatermark ? "Actif" : "Inactif"}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <div className="text-xs font-semibold text-primary mb-2">Original (jamais modifié)</div>
          <img src={media.original_cloudinary_url} alt="original" className="w-full aspect-square object-cover rounded border" />
        </div>
        <div>
          <div className="text-xs font-semibold text-primary mb-2">Public (avec watermark si actif)</div>
          <img src={media.public_cloudinary_url || media.original_cloudinary_url} alt="public" className="w-full aspect-square object-cover rounded border" />
        </div>
      </div>
    </div>
  );
}
